import { Link } from 'react-router';
import { alert } from '@/services/alert';
import {
  ActionIcon,
  Anchor,
  Badge,
  Box,
  Button,
  Flex,
  Group,
  Loader,
  Menu,
  Stack,
  Text,
  TextProps,
  Tooltip,
} from '@mantine/core';
import { formatHumanName, getReferenceString, isResource, resolveId } from '@medplum/core';
import { ResourceAvatar, useMedplum } from '@medplum/react';
import {
  IconChevronDown,
  IconCopy,
  IconLink,
  IconPencil,
  IconClipboardText,
  IconTrash,
  IconX,
} from '@tabler/icons-react';
import { Ref, useCallback, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Appointment, OperationOutcome, Patient, Practitioner, Reference } from '@medplum/fhirtypes';
import { DeleteConfirmationPopover } from '@/components/DeleteConfirmationPopover';
import { HighlightableRichText } from '@/components/HighlightableText/HighlightableRichText';
import { getPatientRescheduleLink } from '@bariendo/lib/scheduling';
import { getConfig } from '@/config';
import { AppointmentDetailData, PopoverPosition } from '../types';
import {
  GROUP_APPOINTMENT_CODES,
  APPOINTMENT_SERIES_IDENTIFIER_SYSTEM,
  APPOINTMENT_STATUS_OPTIONS,
  TIMELINE_CREATE_ITEM_QUERY_PARAM,
  TIMELINE_FOCUS_ITEM_QUERY_PARAM,
} from '@bariendo/lib/constants';
import { ChartingLinkTarget, resolveChartingLink } from '../utils/chartingLink';
import { getAppointmentSeriesIndex } from '@bariendo/lib/scheduling';
import {
  buildCancelAppointmentBundle,
  getAppointmentStatusDisplay,
  hasRichTextContent,
  isAppointmentStatusOptionSelected,
} from '../utils/appointments';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useIsTruncated } from '@/hooks/useIsTruncated';
import { useAppointment } from '@/hooks/useAppointment';
import { useZoomHostLink } from '@/hooks/useZoomHostLink';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { usePopover } from '../hooks/usePopover';
import { getCodeableConceptCode } from '@bariendo/lib/services';

dayjs.extend(utc);
dayjs.extend(timezone);

interface AppointmentDetailPopoverProps {
  opened: boolean;
  onClose: () => void;
  appointment: AppointmentDetailData;
  selectedTimezone: string;
  onDelete?: () => void;
  onEdit?: () => void;
  onStatusChange?: (updatedAppointment: Appointment) => void;
  position: PopoverPosition;
}

const MAX_VISIBLE_PARTICIPANTS = 5;

type Participant = Patient | Practitioner | Reference<Patient> | Reference<Practitioner>;

function LabelText({ children, ...props }: TextProps & { children: React.ReactNode }) {
  return (
    <Text fw={500} w={140} size="sm" {...props}>
      {children}
    </Text>
  );
}

function ParticipantDisplayName({ label, url }: { label: string; url?: string }) {
  const textRef = useRef<HTMLDivElement | HTMLAnchorElement | null>(null);
  const isTruncated = useIsTruncated(textRef, [label]);

  return (
    <Tooltip label={label} withArrow position="top" zIndex={1100} disabled={!isTruncated}>
      {url ? (
        <Anchor ref={textRef as Ref<HTMLAnchorElement>} component={Link} to={url} fz="sm" maw={180}>
          {label}
        </Anchor>
      ) : (
        <Text ref={textRef as Ref<HTMLDivElement>} size="sm" truncate maw={180}>
          {label}
        </Text>
      )}
    </Tooltip>
  );
}

function ParticipantList({
  appointment,
  participantType,
  fallbackName,
}: {
  appointment: AppointmentDetailData;
  participantType: 'providers' | 'patients';
  fallbackName: string;
}) {
  const participants = appointment[participantType];

  const isGroupAppointment = useMemo(() => {
    const appointmentTypeCode = getCodeableConceptCode(
      appointment.appointmentResource.appointmentType
    ) as (typeof GROUP_APPOINTMENT_CODES)[number];
    return GROUP_APPOINTMENT_CODES.includes(appointmentTypeCode);
  }, [appointment.appointmentResource.appointmentType]);

  const getDisplayName = (p: Participant) =>
    (p as Reference<Patient | Practitioner>)?.display ??
    formatHumanName((p as Patient | Practitioner)?.name?.[0]) ??
    fallbackName;

  const visible = participants.slice(0, MAX_VISIBLE_PARTICIPANTS);
  const hidden = participants.slice(MAX_VISIBLE_PARTICIPANTS);

  return (
    <Group gap="xs" wrap="wrap" flex={1}>
      {visible.map((participant, idx) => {
        const url =
          isResource(participant, 'Patient') && !isGroupAppointment
            ? `/Patient/${resolveId(participant)}/timeline`
            : undefined;

        return (
          <Group key={idx} gap="xs" wrap="nowrap" align="center" style={{ flexShrink: 0 }}>
            <ResourceAvatar value={participant} radius="xl" size="sm" />
            <ParticipantDisplayName label={getDisplayName(participant)} url={url} />
          </Group>
        );
      })}
      {hidden.length > 0 && (
        <Tooltip label={hidden.map(getDisplayName).join(', ')} multiline withArrow zIndex={1100}>
          <Text size="xs" c="dimmed" style={{ cursor: 'default' }}>
            +{hidden.length} more
          </Text>
        </Tooltip>
      )}
    </Group>
  );
}

const CHARTING_LINK_LABELS: Record<ChartingLinkTarget['kind'], string> = {
  'chart-the-call': 'Chart the call',
  'view-chart': 'View chart',
  'create-chart-note': 'Create a new chart note',
};

function ChartingLink({ appointment }: { appointment: AppointmentDetailData }) {
  const resource = appointment.appointmentResource;
  const {
    currentUserPermissions: { canCreatePatientAccessNote, canViewClinicalRecord },
  } = useCurrentUser();

  if (appointment.patients.length !== 1 || !resource.id || !!appointment.partner) {
    return null;
  }

  const link = resolveChartingLink(resource, { canCreatePatientAccessNote, canViewClinicalRecord });

  if (!link) {
    return null;
  }

  const patientId = resolveId(appointment.patients[0]);
  const searchParams = new URLSearchParams();

  if (link.kind === 'create-chart-note') {
    searchParams.set(TIMELINE_CREATE_ITEM_QUERY_PARAM, 'chart');
    searchParams.set('appointment', resource.id);
  } else {
    searchParams.set(TIMELINE_FOCUS_ITEM_QUERY_PARAM, link.focus);
  }

  return (
    <Flex gap="md" align="flex-start">
      <LabelText>Chart</LabelText>
      <Flex gap="xs" align="center">
        <IconClipboardText size={16} />
        <Anchor component={Link} to={`/Patient/${patientId}/timeline?${searchParams.toString()}`} fz="sm">
          {CHARTING_LINK_LABELS[link.kind]}
        </Anchor>
      </Flex>
    </Flex>
  );
}

function AppointmentNotes({ appointment }: { appointment: AppointmentDetailData }) {
  const description = appointment.appointmentResource.description;

  const richTextContent = useMemo(() => {
    if (!description) {
      return null;
    }

    try {
      return JSON.parse(description);
    } catch {
      return null;
    }
  }, [description]);

  // Don't render if no description, or if rich text content is empty
  if (!description || (richTextContent && !hasRichTextContent(richTextContent))) {
    return null;
  }

  return (
    <Flex gap="md" align="flex-start">
      <LabelText>Notes</LabelText>
      <Box flex={1} fz="xs">
        {richTextContent ? (
          <HighlightableRichText richTextContent={richTextContent} text="" searchTerm="" size="sm" />
        ) : (
          <Text size="sm">{description}</Text>
        )}
      </Box>
    </Flex>
  );
}

function RescheduleLink({ appointment }: { appointment: AppointmentDetailData }) {
  const patientPortalUrl = getConfig().patientPortalUrl;
  if (!patientPortalUrl) return null;

  const rescheduleLink = getPatientRescheduleLink(patientPortalUrl, appointment.appointmentResource);
  if (!rescheduleLink) return null;

  return (
    <Flex gap="md" align="flex-start">
      <Text fw={500} w={140} size="xs">
        Reschedule Link
      </Text>
      <Flex gap="xs" align="center" flex={1} style={{ minWidth: 0 }}>
        <IconLink size={14} style={{ flexShrink: 0, color: 'var(--mantine-color-gray-5)' }} />
        <Text size="xs" truncate flex={1} style={{ minWidth: 0 }}>
          {rescheduleLink}
        </Text>
        <ActionIcon
          variant="subtle"
          color="gray"
          size="sm"
          onClick={() => {
            navigator.clipboard.writeText(rescheduleLink);
            alert.success('Reschedule link copied to clipboard');
          }}
        >
          <IconCopy size={16} />
        </ActionIcon>
      </Flex>
    </Flex>
  );
}

function ContactType({ appointment }: { appointment: AppointmentDetailData }) {
  const joinUrlForHostFetch = appointment.contactType === 'Video Call' ? appointment.videoCallUrl : undefined;
  const {
    hostUrl: zoomHostUrl,
    isLoading: isZoomHostLoading,
    isWithinOneHour: isWithinZoomHostWindow,
    isPast: isZoomHostPast,
  } = useZoomHostLink(joinUrlForHostFetch, appointment.appointmentResource.start);

  if (!appointment.contactType) {
    return null;
  }

  const renderContactType = (appointment: AppointmentDetailData) => {
    if (appointment.contactType === 'Video Call') {
      const showJoinButton = isZoomHostLoading || Boolean(zoomHostUrl);

      return (
        <Flex gap="xs" align="center">
          <Text flex={1} size="sm">
            {appointment.contactType}
          </Text>
          {showJoinButton && (
            <Button
              size="xs"
              fz="xs"
              variant="filled"
              color="blue"
              radius="lg"
              px={8}
              py="xs"
              styles={{ root: { textDecoration: 'none' } }}
              component={isZoomHostLoading ? 'button' : 'a'}
              href={!isZoomHostLoading && zoomHostUrl ? zoomHostUrl : undefined}
              target={!isZoomHostLoading && zoomHostUrl ? '_blank' : undefined}
              rel={!isZoomHostLoading && zoomHostUrl ? 'noopener noreferrer' : undefined}
              loading={isZoomHostLoading}
            >
              Join
            </Button>
          )}
        </Flex>
      );
    }

    if (appointment.contactType === 'Phone Call') {
      return (
        <Flex gap="xs" align="center">
          <Text flex={1} size="sm">
            {appointment.contactType}
          </Text>
          {appointment.virtualServicePhone && <Text size="sm">{appointment.virtualServicePhone}</Text>}
        </Flex>
      );
    }

    return (
      <Text flex={1} size="sm">
        {appointment.contactType}
      </Text>
    );
  };

  return (
    <>
      <Flex gap="md" align="flex-start">
        <LabelText>Contact Type</LabelText>
        {renderContactType(appointment)}
      </Flex>
      {appointment.contactType === 'Video Call' && (
        <Stack gap="xs" mt={4}>
          {appointment.videoCallUrl && (
            <Stack gap={2}>
              <Text fw={500} size="sm">
                Host URL
              </Text>

              <Flex align="center" gap="xs" w="100%" maw="100%" style={{ minWidth: 0 }}>
                {isWithinZoomHostWindow ? (
                  isZoomHostLoading ? (
                    <Loader size="sm" />
                  ) : zoomHostUrl ? (
                    <>
                      <Anchor
                        href={zoomHostUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        fz="sm"
                        title={zoomHostUrl}
                        style={{
                          flex: 1,
                          minWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {zoomHostUrl}
                      </Anchor>
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        size="sm"
                        flex="0 0 auto"
                        onClick={() => {
                          navigator.clipboard.writeText(zoomHostUrl);
                          alert.success('Copied to clipboard');
                        }}
                      >
                        <IconCopy size={16} />
                      </ActionIcon>
                    </>
                  ) : (
                    <Text size="sm" c="dimmed">
                      Unable to load host link
                    </Text>
                  )
                ) : (
                  <Text size="sm" c="dimmed">
                    {isZoomHostPast ? 'Meeting already happened' : 'Hidden until 1 hour before'}
                  </Text>
                )}
              </Flex>
            </Stack>
          )}

          {appointment.videoCallUrl && (
            <Stack gap={2}>
              <Text fw={500} size="sm">
                Patient URL
              </Text>
              <Flex align="center">
                <Anchor href={appointment.videoCallUrl} target="_blank" fz="sm">
                  {appointment.videoCallUrl}
                </Anchor>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(appointment.videoCallUrl ?? '');
                    alert.success('Copied to clipboard');
                  }}
                >
                  <IconCopy size={16} />
                </ActionIcon>
              </Flex>
            </Stack>
          )}
        </Stack>
      )}
    </>
  );
}

export function AppointmentDetailPopover({
  opened,
  onClose,
  appointment,
  selectedTimezone,
  onDelete,
  onEdit,
  onStatusChange,
  position,
}: AppointmentDetailPopoverProps) {
  const medplum = useMedplum();
  const {
    currentUserPermissions: { canManageSchedules },
    currentUser,
  } = useCurrentUser();
  const { adjustedPosition, arrowSide, popoverWidth, popoverRef } = usePopover(opened, position, onClose);

  const [isDeleting, setIsDeleting] = useState(false);

  const apptResource = appointment.appointmentResource;
  const seriesId = apptResource.identifier?.find((i) => i.system === APPOINTMENT_SERIES_IDENTIFIER_SYSTEM)?.value;
  const currentSeriesIndex = getAppointmentSeriesIndex(apptResource);
  const isSeries = Boolean(seriesId);

  const handleDeleteThis = useCallback(async () => {
    if (!apptResource.id) {
      alert.error('Cannot delete appointment: missing ID');
      return;
    }

    try {
      setIsDeleting(true);
      const bundle = buildCancelAppointmentBundle(apptResource);
      await medplum.executeBatch(bundle);
      alert.success('Appointment deleted successfully');
      onDelete?.();
      onClose();
    } catch (error) {
      if (isResource(error, 'OperationOutcome') as unknown as OperationOutcome) {
        if ((error as OperationOutcome).issue?.some((issue) => issue.code === 'forbidden')) {
          alert.error('You are not authorized to delete this appointment');
          return;
        }
      } else {
        alert.error('Failed to delete appointment: ' + (error instanceof Error ? error.message : 'Unknown error'));
      }
    } finally {
      setIsDeleting(false);
    }
  }, [apptResource, medplum, onClose, onDelete]);

  const deleteSeriesForward = useCallback(async () => {
    if (!apptResource.id || !seriesId) {
      await handleDeleteThis();
      return;
    }
    setIsDeleting(true);
    try {
      const identifierToken = `${APPOINTMENT_SERIES_IDENTIFIER_SYSTEM}|${seriesId}`;
      const serverResults = await medplum.searchResources('Appointment', {
        identifier: identifierToken,
        'status:not': 'cancelled,entered-in-error',
        _count: '1000',
      });
      const candidates = Array.from(serverResults);
      const pivotMs = apptResource.start ? dayjs(apptResource.start).valueOf() : 0;

      const toCancel = candidates.filter((a) => {
        if (!a.id) return false;
        const idx = getAppointmentSeriesIndex(a);
        if (currentSeriesIndex != null && idx != null) {
          return idx >= currentSeriesIndex;
        }
        return a.start ? dayjs(a.start).valueOf() >= pivotMs : false;
      });

      for (const a of toCancel) {
        const bundle = buildCancelAppointmentBundle(a);
        await medplum.executeBatch(bundle);
      }

      alert.success('This appointment and future occurrences were cancelled');
      onDelete?.();
      onClose();
    } catch (error) {
      if (isResource(error, 'OperationOutcome') as unknown as OperationOutcome) {
        if ((error as OperationOutcome).issue?.some((issue) => issue.code === 'forbidden')) {
          alert.error('You are not authorized to delete this appointment');
          return;
        }
      } else {
        alert.error('Failed to delete series: ' + (error instanceof Error ? error.message : 'Unknown error'));
      }
    } finally {
      setIsDeleting(false);
    }
  }, [apptResource.id, apptResource.start, currentSeriesIndex, handleDeleteThis, medplum, onClose, onDelete, seriesId]);

  const deleteSeriesAll = useCallback(async () => {
    if (!seriesId) {
      await handleDeleteThis();
      return;
    }
    setIsDeleting(true);
    try {
      const identifierToken = `${APPOINTMENT_SERIES_IDENTIFIER_SYSTEM}|${seriesId}`;
      const serverResults = await medplum.searchResources('Appointment', {
        identifier: identifierToken,
        'status:not': 'cancelled,entered-in-error',
        _count: '1000',
      });
      const candidates = Array.from(serverResults);

      for (const a of candidates) {
        if (!a.id) continue;
        const bundle = buildCancelAppointmentBundle(a);
        await medplum.executeBatch(bundle);
      }

      alert.success('All occurrences in the series were cancelled');
      onDelete?.();
      onClose();
    } catch (error) {
      if (isResource(error, 'OperationOutcome') as unknown as OperationOutcome) {
        if ((error as OperationOutcome).issue?.some((issue) => issue.code === 'forbidden')) {
          alert.error('You are not authorized to delete this appointment');
          return;
        }
      } else {
        alert.error('Failed to delete series: ' + (error instanceof Error ? error.message : 'Unknown error'));
      }
    } finally {
      setIsDeleting(false);
    }
  }, [handleDeleteThis, medplum, onClose, onDelete, seriesId]);

  const { updateStatus, isUpdatingStatus } = useAppointment(apptResource, onStatusChange);

  const formatTime = (start: string | undefined, end: string | undefined): string => {
    if (!start) {
      return '';
    }
    const startTime = dayjs(start).tz(selectedTimezone).format('h:mma').replace(':00', '');
    if (!end) {
      return startTime;
    }
    const endTime = dayjs(end).tz(selectedTimezone).format('h:mma').replace(':00', '');
    return `${startTime} - ${endTime}`;
  };

  const appointmentTitle = appointment.title || 'Appointment';
  const dateStr = apptResource.start ? dayjs(apptResource.start).tz(selectedTimezone).format('MMMM D, YYYY') : '';
  const timeStr = formatTime(apptResource.start, apptResource.end);

  const currentUserRef = currentUser ? getReferenceString(currentUser as Practitioner) : undefined;
  const isAppointmentProvider =
    !!currentUserRef && apptResource.participant?.some((p) => p.actor?.reference === currentUserRef);

  const canEditAppointment = isAppointmentProvider || canManageSchedules;

  if (!opened) return null;

  const popoverContent = (
    <Box
      ref={popoverRef}
      style={{
        position: 'fixed',
        left: adjustedPosition.x,
        top: adjustedPosition.y,
        transform: 'translateY(-50%)',
        zIndex: 1000,
        width: popoverWidth,
        maxWidth: 'calc(100vw - 32px)', // Ensure it never exceeds viewport with padding
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        padding: '16px',
        pointerEvents: 'auto',
      }}
    >
      {/* Arrow pointing to the event */}
      {arrowSide === 'left' && (
        <Box
          style={{
            position: 'absolute',
            left: '-8px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: 0,
            height: 0,
            borderTop: '8px solid transparent',
            borderBottom: '8px solid transparent',
            borderRight: '8px solid white',
            filter: 'drop-shadow(-2px 0 2px rgba(0, 0, 0, 0.1))',
          }}
        />
      )}
      {arrowSide === 'right' && (
        <Box
          style={{
            position: 'absolute',
            right: '-8px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: 0,
            height: 0,
            borderTop: '8px solid transparent',
            borderBottom: '8px solid transparent',
            borderLeft: '8px solid white',
            filter: 'drop-shadow(2px 0 2px rgba(0, 0, 0, 0.1))',
          }}
        />
      )}
      <Stack gap="md">
        {/* Header with title and actions */}
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Box flex={1} maw="100%" style={{ minWidth: 0 }}>
            {/* Title */}
            <Text fw={700} size="md">
              {appointmentTitle}
            </Text>
            {/* Date and Time */}
            <Text fw={500} mt={4} size="sm">
              {dateStr} • {timeStr}
            </Text>
          </Box>
          <Group gap="xs" style={{ flexShrink: 0 }} wrap="nowrap" align="center">
            {(isAppointmentProvider || canManageSchedules) &&
              (isSeries ? (
                <Menu position="bottom-end" withinPortal={false}>
                  <Menu.Target>
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      size="sm"
                      aria-label="Delete appointment"
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown style={{ zIndex: 1100 }} py="xs">
                    <Menu.Item onClick={() => void handleDeleteThis()} disabled={isDeleting}>
                      Delete this occurrence
                    </Menu.Item>
                    <Menu.Item onClick={() => void deleteSeriesForward()} disabled={isDeleting}>
                      Delete this and upcoming occurrences
                    </Menu.Item>
                    <Menu.Item onClick={() => void deleteSeriesAll()} disabled={isDeleting}>
                      Delete all occurrences in series
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              ) : (
                <DeleteConfirmationPopover
                  onConfirm={handleDeleteThis}
                  isLoading={isDeleting}
                  title="Delete appointment?"
                  description="You cannot undo this action."
                  opened={undefined}
                  onClose={undefined}
                >
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    size="sm"
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </DeleteConfirmationPopover>
              ))}
            {onEdit && (
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                onClick={onEdit}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <IconPencil size={16} />
              </ActionIcon>
            )}
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              onClick={onClose}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <IconX size={16} />
            </ActionIcon>
          </Group>
        </Group>

        {/* Provider */}
        {appointment.providers && appointment.providers.length > 0 && (
          <Flex gap="md" align="flex-start">
            <LabelText>Provider</LabelText>
            <ParticipantList appointment={appointment} participantType="providers" fallbackName="Unknown provider" />
          </Flex>
        )}

        {/* Partner */}
        {appointment.partner && (
          <Flex gap="md" align="flex-start">
            <LabelText>Partner</LabelText>
            <Group gap="xs" wrap="nowrap" align="center" style={{ flexShrink: 0 }}>
              <ResourceAvatar value={appointment.partner.resource} radius="xl" size="sm" />
              <Text size="sm">{appointment.partner.resource?.name ?? 'Unknown partner'}</Text>
            </Group>
          </Flex>
        )}

        {/* Patient */}
        {appointment.patients && appointment.patients.length > 0 && (
          <Flex gap="md" align="flex-start">
            <LabelText>Patient</LabelText>
            <ParticipantList appointment={appointment} participantType="patients" fallbackName="Unknown patient" />
          </Flex>
        )}

        {/* Location */}
        {(appointment.location || appointment.partner?.resource?.address?.[0]) && (
          <Flex gap="md" align="flex-start">
            <LabelText>Location</LabelText>
            <Text flex={1} size="sm">
              {appointment.location || appointment.partner?.resource?.address?.[0]?.text}
            </Text>
          </Flex>
        )}

        {/* Appointment Type */}
        {appointment.appointmentType && (
          <Flex gap="md" align="flex-start">
            <LabelText>Appointment Type</LabelText>
            <Badge variant="light" color="blue" size="sm" fz="10px">
              {appointment.appointmentType}
            </Badge>
          </Flex>
        )}

        {/* Status */}
        {appointment.appointmentResource?.status && (
          <Flex gap="md" align="flex-start">
            <LabelText>Status</LabelText>
            {canEditAppointment ? (
              <Menu position="bottom-start" withArrow withinPortal={false} styles={{ item: { fontSize: '0.75rem' } }}>
                <Menu.Target>
                  <Badge
                    variant="light"
                    color="blue"
                    size="sm"
                    fz="10px"
                    rightSection={<IconChevronDown size={10} />}
                    style={{ cursor: isUpdatingStatus ? 'not-allowed' : 'pointer' }}
                  >
                    {getAppointmentStatusDisplay(appointment.appointmentResource)}
                  </Badge>
                </Menu.Target>
                <Menu.Dropdown style={{ zIndex: 1100 }} py="xs">
                  {APPOINTMENT_STATUS_OPTIONS.map((option) => {
                    const resource = appointment.appointmentResource;
                    const isCurrentStatus = isAppointmentStatusOptionSelected(resource, option.value);
                    return (
                      <Menu.Item
                        key={option.value}
                        onClick={() => updateStatus(option.value)}
                        disabled={isUpdatingStatus || isCurrentStatus}
                      >
                        {option.label}
                      </Menu.Item>
                    );
                  })}
                </Menu.Dropdown>
              </Menu>
            ) : (
              <Badge variant="light" color="blue" size="sm" fz="10px">
                {getAppointmentStatusDisplay(appointment.appointmentResource)}
              </Badge>
            )}
          </Flex>
        )}

        {/* Charting */}
        <ChartingLink appointment={appointment} />

        {/* Contact Type */}
        <ContactType appointment={appointment} />

        {/* Reschedule Link */}
        <RescheduleLink appointment={appointment} />

        {/* Notes */}
        <AppointmentNotes appointment={appointment} />
      </Stack>
    </Box>
  );

  return createPortal(popoverContent, document.body);
}
